/* eslint-disable react-hooks/rules-of-hooks */
import './ApiDropdownFilterComponent.scss'
import React, {useCallback, useEffect, useMemo} from "react";
import type {ButtonProps} from "@mui/material";
import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Popover,
    Typography,
} from "@mui/material";
import type {PageResponse} from "../../../services/ApiTypes";
import {ArrowDropDown, Cancel, CheckBox, CheckBoxOutlineBlank, FilterAlt} from "@mui/icons-material";
import {ApiDropdownSearchComponent} from './ApiDropdownSearchComponent';
import type {Option, OptionPage, SelectionResult} from "./ApiDropdownTypes";
import {MultiSelection} from "./ApiDropdownTypes";
import {plurals} from "../../../utils/js-utils";
import type {ListChildComponentProps} from 'react-window';
import {FixedSizeList} from 'react-window';
import {CurrencyComponent} from "../../currency-component/CurrencyComponent";
import {environment} from "../../../env";

/**
 * If by default everything should be selected
 */
export const API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL = true;

export const DEFAULT: SelectionResult<any> = {
    inclusive: API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL,
    selected: [],
}

type Prop = {
    /**
     * In case of a controlled `selection`
     */
    selection?: SelectionResult
    defaultSelection?: Partial<SelectionResult> | null
    onSelectionChange: (selection: SelectionResult) => void
    getOptions: (search: string) => (Promise<PageResponse<Option>> | null)
    showOptionSubLabel?: boolean
    label: string
    labelPlural?: string
    enableLabelStatus?: boolean
    buttonProps?: ButtonProps
}

/**
 * Based on https://v5.mui.com/material-ui/react-menu/#menulist-composition
 */
export const ApiDropdownFilterComponent: React.FC<Prop> = ({
                                                               selection,
                                                               defaultSelection,
                                                               onSelectionChange,
                                                               getOptions,
                                                               showOptionSubLabel,
                                                               label,
                                                               labelPlural,
                                                               enableLabelStatus,
                                                               buttonProps,
                                                           }) => {
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    const [open, setOpen] = React.useState(false);

    // Check for controlled vs uncontrolled component
    const isControlled = selection !== undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const wasControlled = useMemo(() => isControlled, []);
    if (isControlled !== wasControlled) {
        throw Error('ApiDropdownFilterComponent: selection cannot change from controlled to uncontrolled or vice versa!');
    }

    /**
     * The state that is currently active
     */
    let appliedSelection: SelectionResult;

    let applySelection: (selection: SelectionResult) => void;
    if (isControlled) {
        // controlled component
        appliedSelection = selection;
        applySelection = (s: SelectionResult) => {
            onSelectionChange(s)

            // TODO: Do we need to expect the parent to update the selection? Can we remove the next line?
            handleChange(s, options);
        }
    } else {
        // not controlled component
        const [privateSelectionState, setPrivateSelectionState] = React.useState<SelectionResult>({...DEFAULT, ...defaultSelection});
        appliedSelection = privateSelectionState;
        applySelection = (s: SelectionResult) => {
            onSelectionChange(s);
            setPrivateSelectionState(s); // Update the activeSelection state directly
        };
    }

    const [allSelectedState, setAllSelectedState] = React.useState<'all' | 'none' | 'intermediate' | null>(null);

    /**
     * The state that is currently being modified by the user
     */
    const [nextSelection, setNextSelection] = React.useState<SelectionResult>(appliedSelection);
    const nSelected = nextSelection.selected.length;
    const nextSelectionIsActive = MultiSelection.isActive(nextSelection);

    const handleChange = useCallback((newSelection: SelectionResult, options: OptionPage | null) => {
        const allSelectedState = getAllSelectedState(newSelection, options);

        if (!options?.search) {
            // Normalize the ambiguous cases
            if (!newSelection.inclusive && allSelectedState === 'none') {
                // console.log('ApiDropdownFilterComponent: Normalizing selection to NONE');
                newSelection = MultiSelection.NONE;
            }
            if (newSelection.inclusive && allSelectedState === 'all') {
                // console.log('ApiDropdownFilterComponent: Normalizing selection to ALL');
                newSelection = MultiSelection.ALL;
            }
        }
        setOptions(options);
        setAllSelectedState(allSelectedState)
        setNextSelection(newSelection);
        setTouched(true);
    }, [])

    const handleOptionsChange = useCallback((options: OptionPage | null) => {
        console.log('ApiDropdownFilterComponent: handleOptionsChange', nextSelection, options);
        handleChange(nextSelection, options);
    }, [nextSelection, handleChange]);

    useEffect(() => {

        // TODO: Ensure we correctly apply the new selection
        console.log('ApiDropdownFilterComponent: useEffect appliedSelection', label, Boolean(appliedSelection.inclusive), Number(appliedSelection.selected.length));

        // When the current selection changes, update also the state that the user is modifying
        setNextSelection(appliedSelection);
    }, [appliedSelection]);

    /**
     * If the user has modified anything
     *
     * As a performance optimization we can discard changes that are effectively not changing the selection
     */
    const [touched, setTouched] = React.useState(false);

    const [options, setOptions] = React.useState<OptionPage | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [loadingSearch, setLoadingSearch] = React.useState(false);

    /**
     * Unfiltered total
     */
    const [total, setTotal] = React.useState<null | number>(null);


    useEffect(() => {
        (async () => {
            const promise = getOptions('');
            if (promise === null) {
                return;
            }
            setLoading(true);
            const resp = await promise;
            // TODO: What happens if the selection is outside of the current options?
            setLoading(false);
            handleChange(nextSelection, {
                search: '',
                page: 1,
                window: resp.results,
                total: resp.count
            });
            setTotal(resp.count)
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getOptions])

    const onToggleAllSelected = () => {
        if (options?.search) {
            // If we are in search mode, we work with the current view
            const window = options.window.map(o => o.id);
            if (allSelectedState === 'all') {
                // Remove the current view from the selection
                handleChange(MultiSelection.removeSelection(nextSelection, window), options);
            } else {
                // Add the current view to the selection
                handleChange(MultiSelection.addSelection(nextSelection, window), options);
            }
        } else {
            if (allSelectedState === 'all') {
                // If all are selected, we toggle to none
                handleChange(MultiSelection.NONE, options);
            } else {
                // If none are selected, we toggle to all
                handleChange(MultiSelection.ALL, options);
            }
        }
    }

    const handleOpen = () => {
        setTouched(false)
        setOpen(true);
    };

    const handleClose = () => {
        if (touched) {
            applySelection(nextSelection);
        }
        setTouched(false);
        setOpen(false);
    };

    const cancelFilter = () => {
        applySelection(DEFAULT);

        setTouched(false);
        setOpen(false);
    }

    const onClickElement = (o: Option, index: number) => {
        let newSelected: SelectionResult['selected'];
        if (index !== -1) {
            // Remove element at place index
            newSelected = [...nextSelection.selected];
            newSelected.splice(index, 1);
        } else {
            newSelected = [...nextSelection.selected, o];
        }

        handleChange({
            ...nextSelection,
            selected: newSelected,
        }, options)
    }

    const onClickElementOnly = (o: Option) => {
        handleChange({
            inclusive: true,
            selected: [o],
        }, options)
    }

    const onPressSearchEnter = useCallback(() => {
        if (!options) return;
        if (!nextSelection.inclusive) return;

        const window = options.window.map(o => o.id);
        // Add the current view to the selection
        handleChange(MultiSelection.addSelection(nextSelection, window), options);

         
    }, [options, nextSelection, handleChange])

    const labelPlural_ = labelPlural ?? label + 's';

    let buttonLabel: React.ReactNode;
    if (!enableLabelStatus) {
        buttonLabel = label;
    } else {
        const nAppliedSelected = appliedSelection.selected.length;
        if (nAppliedSelected) {
            if (appliedSelection.inclusive) {
                buttonLabel = `${nAppliedSelected} ${plurals(nAppliedSelected, label, labelPlural_)}`;
            } else {
                buttonLabel = `Excluding ${nAppliedSelected} ${plurals(nAppliedSelected, label, labelPlural_)}`;
            }
        } else {
            buttonLabel = label;
        }
    }

    const appliedSelectionIsActive = MultiSelection.isActive(appliedSelection);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const _FixedSizeList = FixedSizeList as unknown as React.FC<any>;

    return <>
        <Button
            className="api-dropdown-filter-component-button"
            ref={anchorRef}
            variant={appliedSelectionIsActive ? 'contained' : 'outlined'}
            disableElevation
            onClick={handleOpen}
            endIcon={enableLabelStatus ? <ArrowDropDown/> : <FilterAlt/>}
            {...buttonProps}
        >
            {buttonLabel}
            {environment.isTest && ` [${isControlled ? 'C' : 'U'}]`}
        </Button>

        <Popover
            open={open}
            anchorEl={anchorRef.current}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
            }}
            onClose={handleClose}
            disableEnforceFocus
            disableRestoreFocus
        >
            <List>

                <ListItem
                    sx={(theme) => ({
                        background: nextSelectionIsActive ? theme.palette.primary.main : 'unset',
                        color: nextSelectionIsActive ? theme.palette.primary.contrastText : 'unset',
                    })}
                    secondaryAction={<>
                        {loading && <CircularProgress size={24}/>}
                        {(nextSelectionIsActive || appliedSelectionIsActive) &&
                            <IconButton onClick={cancelFilter} size="small" color="inherit"><Cancel/></IconButton>}
                    </>}>
                    <ListItemText
                        sx={{height: '1.5em'}}
                        primary={nextSelectionIsActive ? (
                            nextSelection.inclusive
                                ? `Selected ${nSelected} ` + plurals(nSelected, label, labelPlural_)
                                : `Excluding ${nSelected} ` + plurals(nSelected, label, labelPlural_)
                        ) : null}
                        secondary={!nextSelectionIsActive ? `No filter on ${labelPlural_}` : null}
                    />
                </ListItem>

                <Divider variant="fullWidth" component="li"/>

                {environment.isTest && <ListItem>
                    nextSelection: {'' + nextSelection.selected.length} [{nextSelection.inclusive ? 'in' : 'ex'}]<br/>
                </ListItem>}

                <ListItem>
                    <ApiDropdownSearchComponent
                        options={options}
                        setOptions={handleOptionsChange}
                        getOptions={getOptions}
                        isLoading={loadingSearch}
                        setIsLoading={setLoadingSearch}
                        onPressEnter={onPressSearchEnter}
                    />
                </ListItem>


                <ListItem disablePadding disableGutters>
                    <ListItemButton
                        dense
                        onClick={() => onToggleAllSelected()}
                        disabled={loading || loadingSearch}>
                        <Checkbox
                            edge="start"
                            checked={allSelectedState === 'all' || allSelectedState === 'intermediate'}
                            indeterminate={allSelectedState === 'intermediate'}
                            disabled={allSelectedState === null}
                            onChange={() => onToggleAllSelected()}
                            tabIndex={-1}

                            disableRipple
                            sx={{height: '1em'}}
                        />
                        <ListItemText
                            secondary="Select all"
                        />
                    </ListItemButton>
                </ListItem>


                <ListItem disablePadding disableGutters>
                    <List sx={{width: '30em'}}>
                        <_FixedSizeList
                            height={300}
                            itemCount={options?.window.length ?? 0}
                            itemSize={!showOptionSubLabel ? 46 : 64}
                            width="100%"
                            overscanCount={5}
                            itemData={{
                                options: options?.window ?? [],
                                nextSelection,
                                onClickElement,
                                onClickElementOnly,
                                showOptionSubLabel: Boolean(showOptionSubLabel),
                            }}
                            itemKey={(index, data) => String(data.options[index].id)}
                        >
                            {ApiDropdownFilterComponentRow as any}
                        </_FixedSizeList>
                        {(options?.window.length === 0) && <ListItem>
                            <ListItemText primary={`No results`}/>
                        </ListItem>}
                    </List>
                </ListItem>
                {options &&
                    <ListItem disableGutters disablePadding sx={{padding: '0 1em'}} secondaryAction={
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            size="small"
                            sx={{mr: 1}}>
                            Ok
                        </Button>
                    }>
                        {options.window.length < options.total &&
                            <ListItemText
                                secondary={`Showing ${options.window.length} of ${options.total} search results`}/>
                        }
                        {options.window.length === options.total &&
                            <ListItemText secondary={`${options.window.length} search results`}/>
                        }
                    </ListItem>
                }
            </List>
        </Popover>
    </>
}

type ApiDropdownFilterComponentOptionsProps = {
    options: Option[]
    nextSelection: SelectionResult
    onClickElement: (o: Option, index: number) => void
    onClickElementOnly: (o: Option) => void
    showOptionSubLabel: boolean
};
type C = React.NamedExoticComponent<ListChildComponentProps<ApiDropdownFilterComponentOptionsProps>>;
const ApiDropdownFilterComponentRow: C = React.memo(({index, style, data}) => {
    const {options, nextSelection, onClickElement, onClickElementOnly, showOptionSubLabel} = data;
        const option = options[index];
    const selectedIndex = nextSelection.selected.findIndex(s => s.id === option.id);
    const checked = nextSelection.inclusive ? selectedIndex !== -1 : selectedIndex === -1;
        return <ListItem
            // react-window
            key={index}
            component="div"
            style={style as any}
            disablePadding

            className="api-dropdown-filter-component-item-root"
            disableGutters
            secondaryAction={
                <Button
                    onClick={() => onClickElementOnly(option)}
                    className="show-on-hover"
                    variant="outlined"
                    size="small">
                    Only
                </Button>
            }
        >
            <ListItemButton
                dense
                onClick={() => onClickElement(option, selectedIndex)}>
                <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small"/>}
                    checkedIcon={<CheckBox fontSize="small"/>}
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                />
                <Grid container alignItems="center" className="list-root">
                    <Grid size={9}>
                        <Typography>
                            {option.label}
                            {showOptionSubLabel && <>
                                <br/>
                                <small>{option.subLabel}</small>
                            </>}
                        </Typography>
                    </Grid>
                    <Grid size={3}>
                        {option.subTotalSpend && <CurrencyComponent v={option.subTotalSpend}/>}
                    </Grid>
                </Grid>
            </ListItemButton>
        </ListItem>;
    }
);

/**
 * @returns [isAllSelected, isNoneSelected, isDisabled]
 */
function getAllSelectedState(selection: SelectionResult, view: OptionPage | null): 'all' | 'none' | 'intermediate' | null {
    if (!view || !selection) return null;
    if (!selection.selected) {
        if (selection.inclusive) return 'none';
        else return 'all';
    }
    const inView = new Set(view.window.map(o => o.id));
    const nInView = view.window.length;
    const overlapInView = selection.selected.filter(s => inView.has(s.id)).length;
    const isAllOverlap = overlapInView === nInView;
    const isNoneOverlap = overlapInView === 0;
    if (selection.inclusive) {
        if (isAllOverlap) return 'all'; // All selected
        else if (isNoneOverlap) return 'none'; // None selected
        else return 'intermediate'; // Some selected, but not all
    } else {
        if (isAllOverlap) return 'none'; // All selected, but we are in exclusive mode
        else if (isNoneOverlap) return 'all'; // None selected, but we are in exclusive mode
        else return 'intermediate'; // Some selected, but not all
    }
}
